import { Box, Button, Loader, Modal, MuiTextField, Typography } from "@tm/components"
import { useLocalization } from "@tm/localization"
import { useEffect, useRef, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { decodeJwtToken, errorHandling, getStoredAuthorization } from "@tm/utils"
import { AuthTokenPayload } from "@tm/models"
import { useUser } from "@tm/context-distribution"
import { changeUserEmail, setUser } from "../../../data/repositories/user"
import { useUserSettingsStore } from "../business"
import { EmailFormValues, ProfileFormValues, useEmailSchema, useProfileSchema } from "../business/userSchema"

export type Props = {
    username: string
    initialName: string
    initialEmail: string
}

export function EditProfileModal({ username, initialName, initialEmail }: Props) {
    const { translateText } = useLocalization()
    const { userContext, updateUserContextGeneralContactName } = useUser()

    const { initialFirstName, initialLastName } = getInitialName()
    const [emailSavingActive, setEmailSavingActive] = useState(false)
    const [profileSavingActive, setProfileSavingActive] = useState(false)
    const [emailTextFieldFocused, setEmailTextFieldFocused] = useState(false)

    const [editProfileModalOpen, setEditProfileModalOpen, closeSettingsOnCloseEditProfile, setOpenOrToggleUserSettings] = useUserSettingsStore(
        (store) => [
            store.state.editProfileModalOpen,
            store.actions.setEditProfileModalOpen,
            store.state.closeSettingsOnCloseEditProfile,
            store.actions.setOpenOrToggleUserSettings,
        ]
    )

    const profileSchema = useProfileSchema()
    const emailSchema = useEmailSchema()

    const profileForm = useForm<ProfileFormValues>({
        resolver: zodResolver(profileSchema),
        defaultValues: { firstName: initialFirstName, lastName: initialLastName },
        mode: "onChange",
    })

    const emailForm = useForm<EmailFormValues>({
        resolver: zodResolver(emailSchema),
        defaultValues: { email: initialEmail },
        mode: "onChange",
    })

    const emailInputRef = useRef<HTMLInputElement | null>(null)

    useEffect(() => {
        if (!initialEmail && emailInputRef.current) {
            emailInputRef.current.focus()
        }
    }, [initialEmail])

    function getInitialName() {
        const splittedName = initialName.split(";")
        if (splittedName.length !== 2) {
            return { initialFirstName: "", initialLastName: "" }
        }
        return { initialFirstName: splittedName[0], initialLastName: splittedName[1] }
    }

    async function saveProfile(data: ProfileFormValues) {
        if (!data.firstName || data.firstName.length === 0 || !data.lastName || data.lastName.length === 0 || !userContext.account?.lfdNr) {
            return
        }
        setProfileSavingActive(true)
        await setUser(data.firstName, data.lastName, userContext.account?.lfdNr)
        await updateUserContextGeneralContactName(`${data.firstName};${data.lastName}`)
        setProfileSavingActive(false)
    }

    async function saveEmail(data: EmailFormValues) {
        const auth = getStoredAuthorization()
        const { catalogname } = decodeJwtToken<AuthTokenPayload>(auth?.credentials ?? "")

        setEmailSavingActive(true)
        const response = await changeUserEmail(data.email, window.location.hostname, catalogname, username)
        setEmailSavingActive(false)

        if (!response?.success) {
            emailForm.setError("email", { message: response?.message || translateText(1261) })
            setEmailTextFieldFocused(true)
        }
    }

    function onClose() {
        setEditProfileModalOpen(false)
        profileForm.reset({ firstName: initialFirstName, lastName: initialLastName })
        emailForm.reset({ email: initialEmail })
        if (closeSettingsOnCloseEditProfile) {
            setOpenOrToggleUserSettings(false)
        }
    }

    const renderLabel = (label: string) => <Box fontSize={12}>{label} *</Box>

    return (
        <Modal open={editProfileModalOpen} onOutsideClick={onClose}>
            <Box m={1} pt={3} px={3}>
                <Box mb={2}>
                    <Typography variant="h2">{translateText(13887)}</Typography>
                </Box>

                {/* Profile Form */}
                <form onSubmit={profileForm.handleSubmit(saveProfile)}>
                    <Box mb={2}>
                        <Button
                            type="submit"
                            disabled={!profileForm.formState.isDirty || !profileForm.formState.isValid}
                            sx={{ mr: 0.5 }}
                            color="success"
                        >
                            {translateText(9)}
                        </Button>
                        <Button type="button" onClick={() => profileForm.reset()} disabled={!profileForm.formState.isDirty}>
                            {translateText(48)}
                        </Button>
                        {profileSavingActive && <Loader sx={{ span: { mt: 1 }, display: "inline", ml: 1 }} size="small" />}
                    </Box>

                    <Box mb={1}>
                        <Typography variant="h3">{translateText(1126)}</Typography>
                    </Box>

                    <Box>
                        <MuiTextField
                            fullWidth
                            contentEditable={false}
                            InputProps={{ readOnly: true }}
                            sx={{ fontSize: 14 }}
                            label={renderLabel(translateText(186))}
                            size="extralarge"
                            value={username}
                        />
                    </Box>

                    <Box mb={2}>
                        <Typography variant="label">{translateText(13888)}</Typography>
                    </Box>

                    <Controller
                        name="firstName"
                        control={profileForm.control}
                        render={({ field, fieldState }) => (
                            <MuiTextField
                                {...field}
                                {...errorHandling(fieldState)}
                                fullWidth
                                label={renderLabel(translateText(119))}
                                size="extralarge"
                                sx={{ mb: 2, fontSize: 14 }}
                            />
                        )}
                    />

                    <Controller
                        name="lastName"
                        control={profileForm.control}
                        render={({ field, fieldState }) => (
                            <MuiTextField
                                {...field}
                                {...errorHandling(fieldState)}
                                fullWidth
                                label={renderLabel(translateText(104))}
                                size="extralarge"
                                sx={{ mb: 4, fontSize: 14 }}
                            />
                        )}
                    />
                </form>

                {/* Email Form */}
                <Box mb={2}>
                    <Typography variant="h3">{translateText(13889)}</Typography>
                </Box>

                <Box>
                    <Typography display="inline">{translateText(13890)} </Typography>
                    <Typography display="inline" sx={{ textDecoration: "underline" }}>
                        {translateText(13891)}
                    </Typography>
                    <Typography display="inline"> {translateText(13892)}</Typography>
                </Box>

                <form onSubmit={emailForm.handleSubmit(saveEmail)}>
                    <Box mb={2} mt={3}>
                        <Button
                            type="submit"
                            disabled={!emailForm.formState.isDirty || !emailForm.formState.isValid}
                            sx={{ mr: 0.5 }}
                            color="success"
                        >
                            {translateText(9)}
                        </Button>
                        <Button type="button" onClick={() => emailForm.reset()} disabled={!emailForm.formState.isDirty}>
                            {translateText(48)}
                        </Button>
                        {emailSavingActive && <Loader sx={{ span: { mt: 1 }, display: "inline", ml: 1 }} size="small" />}
                    </Box>

                    <Controller
                        name="email"
                        control={emailForm.control}
                        render={({ field, fieldState }) => (
                            <MuiTextField
                                {...field}
                                {...errorHandling(fieldState)}
                                fullWidth
                                size="extralarge"
                                label={renderLabel(translateText(109))}
                                inputRef={emailInputRef}
                                focused={emailTextFieldFocused}
                                onBlur={() => setEmailTextFieldFocused(false)}
                                sx={{ fontSize: 14 }}
                            />
                        )}
                    />
                </form>
            </Box>
        </Modal>
    )
}
